import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { EllipsisVertical, Pencil, Plus, Trash } from 'lucide-react';
import { useState } from 'react';
import { DeleteTool } from '../delete-tool';
import { EditMcpServerModal } from '../edit-mcp-server-modal';
import { ToolCardData } from '@/modules/gpt-chats/types/tools.service.type';
import { useAddOpenTool } from '@/modules/gpt-chats/hooks/use-tools';

import { useQueryClient } from '@tanstack/react-query';
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { EditToolModal } from '../edit-tool-modal';
import { useTranslation } from 'react-i18next';

export const ToolsCardDropDown = ({
  tool,
  copiedBlocksTool,
}: {
  tool: ToolCardData;
  copiedBlocksTool: boolean;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isAdmin = false;
  const { mutateAsync: addOpenTool, isPending: isAdding } = useAddOpenTool();

  const showAddOption = tool.is_open_tool && !copiedBlocksTool;
  const canModify = copiedBlocksTool || !tool.is_open_tool || isAdmin;
  const showEditOption = canModify;
  const showDeleteOption = canModify;

  const [modal, setModal] = useState<
    | { type: 'archive'; data: { is_archived: boolean } }
    | { type: 'delete'; data: null }
    | { type: 'edit'; data: null }
    | null
  >(null);

  const modalChangeHandler = (value: boolean) => {
    if (!value) setModal(null);
  };

  const handleAddOpenTool = async () => {
    try {
      const response = await addOpenTool({ toolId: tool.id });
      if (response.is_success) {
        showSuccessToast({ description: response.detail || t('TOOL_ADDED_SUCCESSFULLY') });
        queryClient.invalidateQueries({ queryKey: ['tools'] });
      } else {
        showErrorToast({ errors: response.detail || t('FAILED_TO_ADD_TOOL') });
      }
    } catch (error) {
      showErrorToast({ errors: ErrorTransformer(error) });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="-mt-1 h-fit w-fit rounded-full p-2">
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {showAddOption && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleAddOpenTool();
              }}
              disabled={isAdding}
            >
              <Plus className="mr-2 aspect-square w-4" />
              <span>{isAdding ? t('ADDING') : t('ADD')}</span>
            </DropdownMenuItem>
          )}

          {showEditOption && (
            <DropdownMenuItem onClick={() => setModal({ type: 'edit', data: null })}>
              <Pencil className="mr-2 aspect-square w-4" />
              <span>{t('EDIT')}</span>
            </DropdownMenuItem>
          )}
          {showDeleteOption && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setModal({ type: 'delete', data: null });
              }}
              className="!text-error"
            >
              <Trash className="mr-2 aspect-square w-4" />
              <span>{t('DELETE')}</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteTool
        toolId={tool.id}
        isBlocksTool={tool.is_open_tool as boolean}
        open={modal?.type === 'delete'}
        onOpenChange={modalChangeHandler}
      />
      {tool.toolType === 'tool' ? (
        <EditToolModal
          open={modal?.type === 'edit'}
          onOpenChange={modalChangeHandler}
          tool={{
            id: tool.id,
            name: tool.name,
            url: '',
            description: tool.description || '',
            status: tool.status,
          }}
          isBlocksTool={tool.is_open_tool as boolean}
          copiedBlocksTool={copiedBlocksTool}
        />
      ) : (
        <EditMcpServerModal
          open={modal?.type === 'edit'}
          onOpenChange={modalChangeHandler}
          tool={{
            id: tool.id,
            name: tool.name,
            url: '',
            description: tool.description || '',
            status: tool.status,
          }}
          isBlocksTool={tool.is_open_tool as boolean}
          copiedBlocksTool={copiedBlocksTool}
        />
      )}
    </>
  );
};
